// My programmes: a row per product the person has, from the Library's own rows and filtering
// (library/model.js ownedProducts), so Account and the Library always agree. That's every paid
// product whose access counts and is secured, active, overdue, suspended or paused, plus an open
// 1-2-1 client space, as the Library draws its tiles. Ended and revoked access draws nothing, and
// a product that hasn't moved into the hub never shows here. On the Library, the Accelerator and
// POYT get a portal card while they're on their own sites, a link out to that site for someone with
// an account there (hub.my_elsewhere; Adam, 10 Oct), which My programmes leaves out too. Pure: no
// network and no React.
//
// "How long it lasts" is the product's terms_line (terms 2.2), with the Accelerator's month of 12
// after it, as Account.dc.html writes it: "12-month membership. Month 3 of 12."
import * as f from '../lib/format.js'
import { ownedProducts, kindLine, termMonth, termWords } from '../library/model.js'
import { balanceAmount, payHref, payLabel } from '../lib/balance.js'
import { COPY } from './copy.js'

// The colour beside each product's name on the board
function dotKey(p) {
  if (p.look === 'funding_engine') return 'fe'
  if (p.look === 'poyt') return p.type === 'masterclass' ? 'masterclass' : 'poyt'
  if (p.look === 'accelerator') return p.type === 'on_demand' ? 'pcfl' : 'acc'
  return 'master'
}

// "Group 2", "Cohort 1, starts 21 October", "On demand", "Masterclass", "Place secured"
function whatYouHave(o, nowMs) {
  const p = o.product
  if (o.access.status === 'secured') return COPY.placeSecured
  if (p.type === 'client_space') return kindLine(p)
  if (o.cohort) {
    const starts = o.cohort.starts_on ? f.calendarDay(o.cohort.starts_on) : null
    if (starts && starts.day > f.uk(nowMs).day) return `${o.cohort.name}, starts ${f.dayMonthLong(starts)}`
    return o.cohort.name
  }
  if (o.access.cohort_name) return o.access.cohort_name
  return kindLine(p)
}

// What the access itself says, as the Library's tile says it (docs/data-model.md 3.2). A secured
// place says when its balance is due and, when Stripe gives it, how much (CLAUDE.md: the balance and
// the date it's due; member review, 11 Oct), never the word deposit.
function statusLine(a, amount = null) {
  if (a.status === 'secured') return a.balance_due_at ? COPY.balanceDue(f.dayMonthLong(a.balance_due_at), amount) : null
  if (a.status === 'overdue') return a.suspend_on ? `Payment overdue. Open until ${f.dayMonth(a.suspend_on)}` : 'Payment overdue'
  if (a.status === 'suspended') return 'Paused until payment'
  if (a.status === 'paused') return a.paused_until ? `Paused until ${f.dayMonth(f.calendarDay(a.paused_until))}` : 'Paused'
  return null
}

// the Accelerator's month as its home and the Library's tile count it (library/model.js termMonth)
function howLong(o, terms, nowMs, d) {
  const p = o.product
  if (p.type === 'client_space') return terms[p.id] || COPY.clientSpaceTerms
  const line = (terms[p.id] || '').trim()
  const term = o.access.status !== 'secured' ? termMonth(d, p, o.access, nowMs) : null
  if (term) return `${line ? line + ' ' : ''}${termWords(term)}.`
  return line
}

// A secured place's balance: its amount from Stripe (library/load.js, stripe-prices) and the place
// secured page's own pay button (lib/balance.js), or no button while there's no payment link
function balanceFor(d, o, email) {
  const amount = balanceAmount(d.balances, o.product.id)
  const place = (d.places || []).find(x => x.product_id === o.product.id)
  const href = payHref(place?.pay_url, email)
  return { amount, pay: href ? { href, label: payLabel(amount) } : null }
}

// certificates: their own (account/load.js); a product's row carries its certificate, which the
// certificate page says stays here "under My programmes, with the download and the check link"
// (Certificate.dc.html). email: theirs, for a secured place's pay button.
export function programmeRows(d, terms, nowMs, certificates = [], email = null) {
  if (!d) return null
  return [...ownedProducts(d, nowMs).values()]
    .filter(o => o.product.moved_in)
    .sort((a, b) => (a.product.sort_order ?? 0) - (b.product.sort_order ?? 0) || a.product.name.localeCompare(b.product.name))
    .map(o => {
      const balance = o.access.status === 'secured' ? balanceFor(d, o, email) : { amount: null, pay: null }
      return {
        id: o.product.id,
        name: o.product.name,
        dot: dotKey(o.product),
        what: whatYouHave(o, nowMs),
        status: statusLine(o.access, balance.amount),
        // the place secured page's pay button, as a link under the line (member review, 11 Oct)
        pay: balance.pay,
        terms: howLong(o, terms, nowMs, d),
        certificate: certificateFor(o.product, certificates),
      }
    })
}

function certificateFor(p, certificates) {
  const c = (certificates || []).find(x => x.product_id === p.id)
  if (!c || !p.path) return null
  return { id: c.id, number: c.number, courseName: c.course_name, checkPath: c.check_path, href: `${p.path}/certificate` }
}

// Each secured place on a product that has moved in, earliest balance first, for Billing's Next
// payment: a balance to pay is never "Nothing due". Each with its amount and the place secured
// page's pay button (member review, 11 Oct).
export function securedBalances(d, nowMs, email = null) {
  if (!d) return []
  return [...ownedProducts(d, nowMs).values()]
    .filter(o => o.product.moved_in && o.access.status === 'secured')
    .map(o => ({ name: o.product.name, dueAt: o.access.balance_due_at || null, ...balanceFor(d, o, email) }))
    .sort((a, b) => (a.dueAt ? Date.parse(a.dueAt) : Infinity) - (b.dueAt ? Date.parse(b.dueAt) : Infinity))
}

// Billing shows only for someone with a Stripe customer of their own (my_access's has_customer)
export function hasCustomer(d) {
  return Boolean(d?.access?.some(a => a.has_customer))
}
